import { useCallback, useEffect, useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import cn from 'classnames';
import { Element } from 'react-scroll';
import { setAppState } from './appSlice';
import DealsList from './features/dealsList/DealsList';
import { selectDealsListPending, selectTotalDealsCount } from './features/dealsList/dealsListSlice';
import DestinationList from './features/destinationList/DestinationList';
import { updateFavoritesList } from './features/favorite/favoriteSlice';
import SeasonalityTutorial from './features/listTutorial/SeasonalityTutorial';
import LocationDeals from './features/locationDeals/LocationDeals';
import { selectLocationDealsList } from './features/locationDeals/locationDealsSlice';
import SoldOutAlert from './features/locationDeals/SoldOutAlert';
import MainFilter from './features/mainFilter/MainFilter';
import { fetchMainFilterData } from './features/mainFilter/mainFilterSlice';
import NoResult from './features/noResult/NoResult';
import PriceGraph from './features/priceGraph';
import SplashScreen from './components/loaders/SplashScreen';
import SecondaryFilter from './features/secondaryFilter/SecondaryFilter';
import MultiRatesFilter from './features/secondaryFilter/MultiRatesFilter';
import { fetchSecondaryFilterData } from './features/secondaryFilter/secondaryFilterSlice';
import { getFavoritesFromLS, isMobile, setCookie, getCookie } from './utilities/helpers';
import useGetDataFromUrl from './hooks/app/useGetDataFromUrl';
import useFetchDeals from './hooks/app/useFetchDeals';
import EducationalMessages from './components/EducationalMessages';
import useTrackUrlParams from './hooks/app/useTrackUrlParams';
import HotelSearchResult from './features/search/HotelSearchResult';
import useEducationalMessages from './hooks/app/useEducationalMessages';
import { selectFilterData, selectFiltersLoaded, specificCitySelectedSelector } from './features/mainFilter/selectors';
import WizardV2 from './features/wizard-v2';
import WizardSkip from './features/wizard-v2/WizardSkip';
import ErrorBoundary from './components/ErrorBoundary';
import ReloadPopup from './components/ReloadPopup/ReloadPopup';
// import EntryPopup from './components/EntryPopup/EntryPopup';
import useWizard from './hooks/app/useWizard';
import useUpdateSecondaryFilterBasenOnWhatFilter from './hooks/secondaryFilter/useUpdateSecondaryFilterBasenOnWhatFilter';
import useGlobalLoading from './hooks/app/useGlobalLoading';
import HintsBlock from './components/HintsBlock';
import AnotherAirportPopup from './components/AnotherAirportPopup';
import CustomizeButton from './features/customize-button/CustomizeButton';
import useInitializeFavorites from './hooks/app/useInitializeFavorites';
import abTesting from './services/abTesting';
import CardsPositionDebug from './components/debugTools/CardsPositionDebug';
import WizardDesktop from './features/wizardDesktop';
import { SwitchTransition } from 'react-transition-group';
import FadeTransition from './components/animations/FadeTransition';
import { setWizardDesktop, setMobileHomepage } from './features/wizard-v2/onboardingSlice';
import useGetNearbyAirports from './hooks/app/useGetNearbyAirport';
import { isEntryFlow, getParamsFromUrl, getParamFromUrl } from './utilities/urlResolver';
import AssistantFeedback from './features/wizard-v2/ai/AssistantFeedback';
import UnsupportedSearch from './features/wizard-v2/UnsupportedSearch';
import useInitializeB2B from './hooks/app/useInitializeB2B';
import Hero from './features/heroSection';
import GroupBookingModal from './features/groupBooking/GroupBookingModal';
import useInitializeUser from './hooks/app/useInitializeUser';
import { Proposal, ProposalProvider, Toaster } from 'travelyo-react-shared-components';

function App() {
  const dispatch = useDispatch();
  const initialRender = useRef(true);
  const mainFilter = useSelector(selectFilterData);
  const filtersReady = useSelector(selectFiltersLoaded);
  const totalDealsCount = useSelector(selectTotalDealsCount);
  const locationDeals = useSelector(selectLocationDealsList);
  const { showSoldOutAlert, hotelPrices } = useSelector(selectLocationDealsList);
  const showOnboardingV2 = useSelector((state) => state.app.showOnboardingV2);
  const showResults = useSelector((state) => state.app.showResults);
  const priceGraphTop = useSelector((state) => state.app.priceGraphTop);
  const showDesktopWizard = useSelector((state) => state.onboarding.showDesktop);
  const showMobileHP = useSelector((state) => state.onboarding.showMobileHP);
  const dealListPending = useSelector(selectDealsListPending);
  const specificCitySelected = useSelector(specificCitySelectedSelector);
  const noNonCachedResults =
    !hotelPrices.pingStatusPending &&
    !hotelPrices.offers.length &&
    (!(hotelPrices.pagination && hotelPrices.pagination.total_offers_pages) || locationDeals.hideSoldOutDeals);
  const noResults = totalDealsCount === 0 && (locationDeals.list === 0 || noNonCachedResults);

  const [showFade, setShowFade] = useState(false);
  const [isVisibleFade, setIsVisibleFade] = useState(false);
  const [sticky, setSticky] = useState(false);
  const { when } = mainFilter.searchEngine;
  const isSpecificDateSelected = !!(when?.type === 'specific' && (when?.startDate || when?.endDate));
  const secondaryFilterLoading = useSelector(state => state.app.globalLoading.secondaryFilterLoading);
  // disabled by Dror request
  // const showAssistantFeedback = !localStorage.getItem('wizardFeedbackAnswered') && !getParamFromUrl('unsupportedSearch');

  const [isWizardSkip, setWizardSkip] = useState(false);

  const showGroupBookingModal = useSelector((state) => state.app.showGroupBookingModal);

  if ('scrollRestoration' in window.history) {
    window.scrollRestoration = 'manual';
  }

  const hideFadeIn = () => {
    setTimeout(() => {
      setIsVisibleFade(false);
      setShowFade(false);
    }, 200);
  };

  const handleShowFade = useCallback(() => {
    if (!specificCitySelected || !isSpecificDateSelected) return;
    setIsVisibleFade(true);
    setShowFade(true);
    setTimeout(hideFadeIn, 200);
  }, [specificCitySelected, isSpecificDateSelected]);

  useGetNearbyAirports();
  useGetDataFromUrl();


  useEffect(() => {
    const onHashChanged = () => {
      // for the wizard skip screen (on second visit)
      if (abTesting.wizardSkip && isMobile()) {
        const hfLastFilters = JSON.parse(localStorage.getItem('hfLastFilters'));
        const hfUser = JSON.parse(localStorage.getItem('hf_user'));
        if (
          !isEntryFlow('any') &&
          (Object.keys(getParamsFromUrl()).length <= 1) &&
          hfLastFilters && hfLastFilters.date &&
          ((Date.now() - hfLastFilters.date)/(1000 * 60 * 60 * 24)) < 30 &&
          hfUser?.user?.email &&
          window.location.href.indexOf('startWizard=true') === -1 &&
          window.location.search.indexOf('wizardWidget=true') === -1
        ) {
          setWizardSkip(true);
        } else if (window.location.href.indexOf('startWizard=true') > -1 || window.location.search.indexOf('wizardWidget=true') > -1) {
          setWizardSkip(false);
        }
        const logo = document.getElementById('logo');
        if (logo && logo.href.indexOf('startWizard=true') === -1) {
          logo.href = `${logo.href}?startWizard=true#startWizard=true`;
        }
      }
    };
    onHashChanged();

    if (window.location.href.indexOf('enableAiAssistantV2=true') > -1 && !getCookie('abTest.aiAssistantV2')) {
      setCookie('abTest.aiAssistantV2', 'true', 1);
    }

    window.addEventListener("hashchange", onHashChanged);

    return () => {
      window.removeEventListener("hashchange", onHashChanged);
    };
  }, []);

  useEffect(() => {
    dispatch(fetchMainFilterData());
    dispatch(fetchSecondaryFilterData());
    dispatch(updateFavoritesList(getFavoritesFromLS()));
  }, [dispatch]);

  useGlobalLoading();
  useInitializeB2B();

  // Manage price graph position.
  useEffect(() => {
    if (isSpecificDateSelected && specificCitySelected) {
      handleShowFade();
      dispatch(setAppState({ priceGraphTop: true }));
    } else {
      dispatch(setAppState({ priceGraphTop: false }));
    }
  }, [dispatch, isSpecificDateSelected, specificCitySelected, when, handleShowFade]);

  useTrackUrlParams();
  useFetchDeals();
  useEducationalMessages();
  useWizard();
  useUpdateSecondaryFilterBasenOnWhatFilter();
  useInitializeFavorites();
  useInitializeUser();

  useEffect(() => {
    if (initialRender.current) {
      dispatch(setWizardDesktop(!showResults));
      initialRender.current = false;
    }
    if (abTesting.mobileHomepageScrollable && isMobile()) {
      dispatch(setMobileHomepage(!showResults));
    }
  }, [dispatch]);

  useEffect(() => {
    const toggleClass = (selector, className, condition) => {
      const elements = document.querySelectorAll(selector);
      for (const element of elements) {
        if (condition) {
          element.classList.add(className);
        } else {
          element.classList.remove(className);
        }
      }
    };

    const toggleHomePageVariants = () => {
      if ((window.innerWidth > 768) || (abTesting.mobileHomepageScrollable && window.innerWidth < 769)) {
        document.getElementById('new-block-area')?.classList?.remove('hidden');
        document.getElementById('block-area')?.classList?.add('hidden');
      } else {
        document.getElementById('new-block-area')?.classList?.add('hidden');
        document.getElementById('block-area')?.classList?.remove('hidden');
      }

      if (showResults) {
        document.getElementById('new-block-area')?.classList?.add('hidden');
        document.getElementById('block-area')?.classList?.add('hidden');
      }
    }

    toggleClass('#app', 'hidden', !isMobile() && showDesktopWizard && !showGroupBookingModal);
    toggleClass('#navbar-header-hf', 'hidden', !isMobile() && showDesktopWizard);
    toggleClass('.wizard-desktop-container', 'hidden', !showDesktopWizard ? !showDesktopWizard : (!isMobile() && !showDesktopWizard));
    toggleHomePageVariants()
    toggleClass('.subscribe-popup', 'hidden', !isMobile() && showDesktopWizard);
  }, [showDesktopWizard, showMobileHP, showResults, showGroupBookingModal]);

  // ! RTL/LTR dev switch
  // useEffect(() => {
  //   if (window.location.hostname === 'localhost') {
  //     document.querySelector('html').setAttribute('dir', 'rtl');
  //     document.querySelector('html').setAttribute('lang', 'he');
  //   }
  // }, []);

  const mainContent = (
    <>
      <div id="main-filter-portal" />
      <AnotherAirportPopup />
      <ReloadPopup />
      {/* !abTesting.desktopNewHomepage && <EntryPopup /> */}
      <Hero>
        {abTesting.customizeButton ? (
          <>
            {!isMobile() && <MainFilter handleShowFade={handleShowFade} sticky={sticky} setSticky={setSticky} />}
            {isMobile() && filtersReady ? <CustomizeButton /> : null}
          </>
        ) : (
          <MainFilter handleShowFade={handleShowFade} sticky={sticky} setSticky={setSticky} />
        )}
      </Hero>
      {!showResults && <HintsBlock beforeSearch={true} />}
      <Element name="scrollToResults" id="scrollToResults" />
      {showResults && (
        <ProposalProvider>
          <div className="results-section animated fadeIn results-section--experimental">
            <div className="c-container">
              <SeasonalityTutorial />
              {/* TODO: add condition for customize button */}
              {secondaryFilterLoading && window.innerWidth <= 768 ? (
                <div className="secondary-filter-content loader">
                  <div className="secondary-filter-loader" />
                  <div className="secondary-filter-loader" />
                  <div className="secondary-filter-loader" />
                  <div className="secondary-filter-loader" />
                  <div className="secondary-filter-loader" />
                  <div className="secondary-filter-loader" />
                </div>
              ) : null}
              {abTesting.customizeButton ? (
                !isMobile() && <SecondaryFilter sticky={sticky} />
              ) : (
                <SecondaryFilter sticky={sticky} />
              )}
              {filtersReady && abTesting.moveMultiRates && (
                <MultiRatesFilter sticky={sticky} />
              )}
              {abTesting.educationalMessages && <EducationalMessages />}
              <div
                className={cn('fade-app-element', {
                  'no-visible': isVisibleFade,
                  hidden: !showFade,
                })}
              />
              <CardsPositionDebug />
              {filtersReady && !priceGraphTop && (
                <>
                  <NoResult />
                    <div className="deal-list c-row">
                      <ErrorBoundary>
                        <HotelSearchResult noResults={noResults} />
                      </ErrorBoundary>
                        <DealsList />
                    </div>
                </>
              )}
              {specificCitySelected ? (
                <>
                  {totalDealsCount > 0 && <PriceGraph handleShowFade={handleShowFade} />}
                  {filtersReady && priceGraphTop && (
                    <>
                      {isSpecificDateSelected && showSoldOutAlert && (
                        <SoldOutAlert />
                      )}
                      {noResults && <NoResult />}
                        <div className="deal-list c-row">
                          <ErrorBoundary>
                            <HotelSearchResult noResults={noResults} />
                          </ErrorBoundary>
                            <DealsList />
                          <ErrorBoundary>
                            <LocationDeals noResults={noResults} />
                          </ErrorBoundary>
                        </div>
                    </>
                  )}
                  {filtersReady && !priceGraphTop && (
                      <LocationDeals noResults={noResults} />
                  )}
                </>
              ) : (
                filtersReady && priceGraphTop && <NoResult />
              )}
              <DestinationList />
            </div>
          </div>
          <Proposal />
        </ProposalProvider>
      )}

      {showOnboardingV2 && (
        <ErrorBoundary>
          <WizardV2 />
        </ErrorBoundary>
      )}
      <SplashScreen />
      {isWizardSkip && <WizardSkip showResults={showResults} loading={dealListPending} filtersReady={filtersReady} />}
      {/* {showAssistantFeedback && <AssistantFeedback />} */}
      <UnsupportedSearch />
      {abTesting.groupBooking && <GroupBookingModal />}
      <div id="sticky-filters-portal" />
      <div id="modal-portal" />
      <Toaster />
    </>
  );

  if (!isMobile()) {
    return (
      <SwitchTransition mode="in-out">
        <FadeTransition
          key={showDesktopWizard}
          addEndListener={(node, done) => node.addEventListener("transitionend", done, false)}
          classNames='fade'
        >
          <div>
            {showDesktopWizard ? <WizardDesktop /> : mainContent}
            {showDesktopWizard && abTesting.groupBooking && <GroupBookingModal />}
            {showDesktopWizard && <div id="modal-portal" />}
          </div>
        </FadeTransition>
      </SwitchTransition>
    )
  }

  return mainContent;
}

export default App;
